import type { ReactElement } from 'react';

export type SortDirection = 'asc' | 'desc';

interface SortableThProps {
  /** Visible column label. */
  label: string;
  /** Stable key this column sorts by. */
  sortKey: string;
  /** Currently active sort key. */
  activeKey: string;
  /** Current sort direction. */
  direction: SortDirection;
  /** Called with this column's key when the header is clicked. */
  onSort: (key: string) => void;
  /** Right-align (numeric columns). */
  align?: 'start' | 'end';
  /** Extra class names for the `<th>`. */
  className?: string;
}

/**
 * Reusable sortable table header.
 *
 * Renders a `<th>` whose entire label is a button. The active column shows a
 * direction caret; inactive columns show a neutral up/down glyph so users can
 * tell every column is sortable. `aria-sort` is set on the `<th>` so screen
 * readers announce the current sort state (WCAG-friendly grid semantics).
 */
export default function SortableTh({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  align = 'start',
  className
}: SortableThProps): ReactElement {
  const active = activeKey === sortKey;
  const ariaSort = active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none';
  const icon = active
    ? direction === 'asc'
      ? 'bi-caret-up-fill'
      : 'bi-caret-down-fill'
    : 'bi-arrow-down-up';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`mh-sortable-th${align === 'end' ? ' text-end' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        className={`mh-sort-btn${active ? ' is-active' : ''}`}
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}${active ? (direction === 'asc' ? ' (ascending)' : ' (descending)') : ''}`}
      >
        <span>{label}</span>
        <i className={`bi ${icon} mh-sort-icon`} aria-hidden="true" />
      </button>
    </th>
  );
}
